import {
    getSelectedCard,
    getStockSelected,
    getUndoButtonSelected,
    getSelectedNewGameButtonSelected,
    getRecordsToggleButtonSelected,
    getMenuButtonSelected,
    getAutoCompleteButtonSelected,
} from './getClickSelection'
import {
    cardIndexFoundation,
    cardIndexTableau,
    cardIndexWaste,
    isCardOverFoundation,
    isCardOverTableau,
    findOverlapFoundation,
    findOverlapTableau,
} from './findCardIndex'
import {
    setDraggedCards,
    getDraggedCards,
    appState,
    getCurrentCardState,
    setAppState,
    commitMove,
    incrementUndoCount,
} from './state'
import { playSound, unlockAudio } from './audioFunctions'
import { Card } from '../Models/Card'
import { schedulePaint } from './paint'
import { deepClone } from './utils/deepClone'
import { startNewGame } from './setupCards'
import { CurrentAppState } from '../Models/AppState'
import { animationManager, animatedUndo } from './animations'
import {
    autocompleteGame,
    undoAutoComplete,
    canAutoComplete,
    hasAutoCompleteSnapshot,
} from './autocompleteGame'
import { setDragPosition, clearAllDragPositions, getCardPosition } from './cardPositions'
import { canPlaceOnFoundation, canPlaceOnTableau, decrementWasteVisible } from './gameRules'
import { findMenuHitArea, executeMenuAction } from './menuActions'
import { setMenuPressedItem, clearMenuPressedItem, resetMenuScreen } from './drawMenu'
import { setButtonPressedItem, clearButtonPressedItem } from './drawButtons'
import { hapticLift, hapticDrop } from './haptics'
import { timerPause, timerResume, timerIsStarted } from './timer'
import { handleWinCheck } from './winCheck'

let startY
let startX
let myLatestTap
let lastTapX = 0
let lastTapY = 0

let mouseDownTime = 0
let lastDoubleTapTime = 0 // Track when we last handled a double-tap
let _isProcessingDoubleTap = false // Reentrant lock to prevent double-fire

/** Reset double-tap detection state. Used by TestRunner to prevent false double-tap triggers. */
export function resetDoubleTapState(): void {
    myLatestTap = 0
    lastTapX = -9999
    lastTapY = -9999
    lastDoubleTapTime = 0
}

/** Convert a MouseEvent into a minimal mock TouchEvent for the unified touch handlers. */
function createMockTouchEvent(e: MouseEvent, type: string) {
    return {
        changedTouches: [
            {
                clientX: e.clientX,
                clientY: e.clientY,
                pageX: e.pageX,
                pageY: e.pageY,
                screenX: e.screenX,
                screenY: e.screenY,
            },
        ],
        preventDefault: () => e.preventDefault(),
        type,
        touches: [
            {
                clientX: e.clientX,
                clientY: e.clientY,
            },
        ],
    }
}

export function canvasMouseDown(e: MouseEvent) {
    e.preventDefault()
    mouseDownTime = new Date().getTime()

    // @ts-ignore - Converting mouse event to touch event format
    canvasTouchStart(createMockTouchEvent(e, 'touchstart'))
}

export function canvasMouseMove(e: MouseEvent) {
    e.preventDefault()
    // @ts-ignore - Converting mouse event to touch event format
    canvasTouchMove(createMockTouchEvent(e, 'touchmove'))
}

export function canvasMouseUp(e: MouseEvent) {
    e.preventDefault()
    // @ts-ignore - Converting mouse event to touch event format
    canvasTouchEnd(createMockTouchEvent(e, 'touchend'))
}

export function canvasMouseDblClick(e: MouseEvent) {
    e.preventDefault()

    // Skip if we already handled this double-tap via the touchstart timing detection
    const timeSinceLastDoubleTap = new Date().getTime() - lastDoubleTapTime
    if (timeSinceLastDoubleTap < 200) {
        return
    }

    // @ts-ignore - Converting mouse event to touch event format
    canvasTouchDoubleTap(createMockTouchEvent(e, 'touchend'))
}

export function canvasTouchStart(e: TouchEvent) {
    // Unlock audio on first user gesture (required by iOS WebKit autoplay policy)
    unlockAudio()

    // Cancel any in-flight animations so the user can interact immediately
    if (animationManager.isAnimating()) {
        animationManager.clearAll()
    }

    const touch = e.changedTouches[0]

    // If menu is open, handle menu interactions via canvas hit areas
    if (appState.currentAppState === CurrentAppState.Menu) {
        // Let the menu button toggle through (handled below)
        const selectedMenuBtn: boolean = getMenuButtonSelected(touch.clientX, touch.clientY)
        if (!selectedMenuBtn) {
            e.preventDefault()
            // Check if we hit a menu control (including "New Game" text button)
            const hitArea = findMenuHitArea(touch.clientX, touch.clientY)
            if (hitArea) {
                // Show press highlight immediately, then execute action after a short delay
                setMenuPressedItem(hitArea.id)
                schedulePaint()
                setTimeout(() => {
                    clearMenuPressedItem()
                    executeMenuAction(hitArea.id)
                    schedulePaint()
                }, 80)
            }
            // Taps outside menu controls are ignored — menu only closes via X button
            return
        }
    }

    e.preventDefault()

    const currentTap = new Date().getTime()
    const timeSince = currentTap - myLatestTap
    const distX = Math.abs(touch.clientX - lastTapX)
    const distY = Math.abs(touch.clientY - lastTapY)
    const isNearLastTap = distX < 50 && distY < 50 // Within 50 pixels of last tap

    // Only treat as double-tap if there's a face-up card at this position
    // AND we're not on the stock pile (stock should always process individual taps).
    // Face-down cards are excluded: they should always go through normal tap handling
    // so they get flipped, even if the user taps quickly after a previous double-tap.
    const potentialCard = getSelectedCard(touch.clientX, touch.clientY)
    const isOnStock = getStockSelected(touch.clientX, touch.clientY)
    if (
        timeSince < 600 &&
        timeSince > 0 &&
        isNearLastTap &&
        potentialCard !== undefined &&
        !potentialCard.isFaceDown &&
        !isOnStock
    ) {
        // double tap - handle it and return to prevent pickup
        canvasTouchDoubleTap(e)
        myLatestTap = new Date().getTime()
        lastDoubleTapTime = new Date().getTime() // Mark that we just handled a double-tap
        return
    }

    myLatestTap = new Date().getTime()
    lastTapX = touch.clientX
    lastTapY = touch.clientY

    startX = touch.clientX
    startY = touch.clientY

    const selectedCard = getSelectedCard(touch.clientX, touch.clientY)
    // console.log({selectedCard})
    const selectedStock: boolean = getStockSelected(touch.clientX, touch.clientY)
    // console.log({ selectedStock })
    const selectedUndo: boolean = getUndoButtonSelected(touch.clientX, touch.clientY)
    // console.log({ selectedUndo })
    const selectedNewGameButton: boolean = getSelectedNewGameButtonSelected(touch.clientX, touch.clientY)
    const selectedRecordsToggleButton: boolean = getRecordsToggleButtonSelected(touch.clientX, touch.clientY)
    const selectedMenuButton: boolean = getMenuButtonSelected(touch.clientX, touch.clientY)
    // console.log({ selectedMenuButton })
    const selectedAutoComplete: boolean =
        canAutoComplete() && getAutoCompleteButtonSelected(touch.clientX, touch.clientY)

    // If menu is open and user clicks on canvas (but not on menu button), close the menu
    // NOTE: This is now handled by the hit-area logic at the top of canvasTouchStart.
    // Keeping the menu button toggle handling below.

    // ── Buttons take priority over cards ──
    // When a long tableau cascade overlaps a button, the button wins.
    // This prevents accidentally picking up a card when the user taps
    // on a visually-on-top button.

    if (selectedMenuButton) {
        setButtonPressedItem('btn-menu')
        schedulePaint()
        setTimeout(() => {
            clearButtonPressedItem()
            const newAppState = deepClone(appState)
            if (newAppState.currentAppState === CurrentAppState.Playing) {
                newAppState.currentAppState = CurrentAppState.Menu
                if (timerIsStarted()) timerPause()
                setAppState(newAppState)
            } else if (newAppState.currentAppState === CurrentAppState.Menu) {
                newAppState.currentAppState = CurrentAppState.Playing
                newAppState.showMenuRecords = false
                resetMenuScreen()
                if (timerIsStarted()) timerResume()
                setAppState(newAppState)
            }
            schedulePaint()
        }, 80)
        return
    }

    if (appState.currentAppState === CurrentAppState.Completed && selectedNewGameButton) {
        setButtonPressedItem('btn-newgame')
        schedulePaint()
        setTimeout(() => {
            clearButtonPressedItem()
            const currentAppState = appState
            currentAppState.currentAppState = CurrentAppState.Playing
            currentAppState.showWinRecords = false
            setAppState(currentAppState)
            startNewGame()
            schedulePaint()
        }, 80)
        return
    }

    if (appState.currentAppState === CurrentAppState.Completed && selectedRecordsToggleButton) {
        setButtonPressedItem('btn-records')
        schedulePaint()
        setTimeout(() => {
            clearButtonPressedItem()
            appState.showWinRecords = !appState.showWinRecords
            schedulePaint()
        }, 80)
        return
    }

    if (appState.currentAppState === CurrentAppState.Playing && selectedNewGameButton) {
        setButtonPressedItem('btn-newgame')
        schedulePaint()
        setTimeout(() => {
            clearButtonPressedItem()
            startNewGame()
            schedulePaint()
        }, 80)
        return
    }

    if (appState.currentAppState === CurrentAppState.Playing && selectedUndo) {
        setButtonPressedItem('btn-undo')
        schedulePaint()
        setTimeout(() => {
            clearButtonPressedItem()
            incrementUndoCount()
            if (hasAutoCompleteSnapshot()) {
                undoAutoComplete()
            } else {
                animatedUndo()
            }
            playSound('undo')
            schedulePaint()
        }, 80)
        return
    }

    if (appState.currentAppState === CurrentAppState.Playing && selectedAutoComplete) {
        setButtonPressedItem('btn-autocomplete')
        schedulePaint()
        setTimeout(() => {
            clearButtonPressedItem()
            autocompleteGame()
            schedulePaint()
        }, 80)
        return
    }

    // ── Card & stock interactions (only if no button was hit) ──

    // 1. If the card is selected, make it the current dragging card
    if (appState.currentAppState === CurrentAppState.Playing && selectedCard !== undefined) {
        const tableauIndex = cardIndexTableau(selectedCard)
        const foundIndex = cardIndexFoundation(selectedCard)
        const wasteIndex = cardIndexWaste(selectedCard)
        if (tableauIndex !== undefined) {
            const [ti, tj] = tableauIndex
            if (!selectedCard.isFaceDown) {
                playSound('lift')
                hapticLift()
                try {
                    let picked: Card[]
                    commitMove(
                        (s) => {
                            picked = s.tableau[ti].slice(tj)
                            s.tableau[ti] = s.tableau[ti].slice(0, tj)
                        },
                        { skipAutoFlip: true, skipIntegrity: true }
                    )
                    setDraggedCards(picked!)
                    schedulePaint()
                } catch (error) {
                    console.error('Error during deep clone or card state update:', error)
                    return
                }
            } else if (selectedCard.isFaceDown && tj === getCurrentCardState().tableau[ti].length - 1) {
                playSound('flip')
                hapticLift()
                try {
                    commitMove((s) => {
                        s.tableau[ti][tj].isFaceDown = false
                    })
                    // Reset tap tracking so the flip doesn't count as the "first tap"
                    // of a double-tap sequence. Without this, tapping the now-face-up
                    // card within 600ms would trigger an unwanted double-tap.
                    myLatestTap = 0
                    schedulePaint()
                } catch (error) {
                    console.error('Error flipping face down card:', error)
                    return
                }
            }
        } else if (foundIndex !== undefined) {
            const [fi, fj] = foundIndex
            // Only allow picking up the top card from a foundation pile
            if (!selectedCard.isFaceDown && fj === getCurrentCardState().foundation[fi].length - 1) {
                playSound('lift')
                hapticLift()
                let picked: Card[]
                commitMove(
                    (s) => {
                        picked = [s.foundation[fi][fj]]
                        s.foundation[fi] = s.foundation[fi].slice(0, fj)
                    },
                    { skipIntegrity: true }
                )
                setDraggedCards(picked!)
                schedulePaint()
            }
        } else if (wasteIndex !== undefined) {
            if (!selectedCard.isFaceDown) {
                playSound('lift')
                hapticLift()
                let picked: Card[]
                commitMove(
                    (s) => {
                        picked = [s.waste[wasteIndex]]
                        s.waste = s.waste.slice(0, wasteIndex)
                        decrementWasteVisible(s, appState.NUM_CARDS_FLIPPED)
                    },
                    { skipIntegrity: true }
                )
                setDraggedCards(picked!)
                schedulePaint()
            }
        } else {
            // No cards in waste pile to pick up
        }
    }

    if (appState.currentAppState === CurrentAppState.Playing && selectedStock) {
        // Don't allow drawing from stock while holding cards
        if (getDraggedCards().length > 0) {
            return
        }

        // if we tapped on stock.... we can't grab one of those cards.. we flip it.
        const currentState = getCurrentCardState()
        if (currentState.stock.length > 0) {
            // something in stock, pop and flip
            // Draw up to NUM_CARDS_FLIPPED cards, or however many remain
            const numCardsToDraw = Math.min(appState.NUM_CARDS_FLIPPED, currentState.stock.length)

            // Save pre-move positions for animation (stock position)
            const stockFromPositions = currentState.stock.slice(-numCardsToDraw).map((c) => {
                const pos = getCardPosition(c)
                return { fromX: pos.x, fromY: pos.y }
            })

            const committed = commitMove((s) => {
                const moveToWaste = s.stock.splice(-numCardsToDraw)
                moveToWaste.forEach((card) => (card.isFaceDown = false))
                s.waste.push(...moveToWaste)
                s.wasteVisibleCount = numCardsToDraw
            })

            // Animate drawn cards from stock position to waste position
            const wasteLen = committed.waste.length
            const animEntries = Array.from({ length: numCardsToDraw }, (_, idx) => ({
                card: committed.waste[wasteLen - numCardsToDraw + idx],
                fromX: stockFromPositions[idx].fromX,
                fromY: stockFromPositions[idx].fromY,
            }))
            animationManager.animateCards(animEntries, 200)
            playSound('draw')
            hapticDrop()
        } else {
            // nothing in stock, reset the hand — move waste back to stock
            // Save pre-move positions of waste cards for animation
            const wasteFromPositions = currentState.waste.map((c) => {
                const pos = getCardPosition(c)
                return { fromX: pos.x, fromY: pos.y }
            })

            const committed = commitMove((s) => {
                const moveToStock = s.waste.map((card) => {
                    card.isFaceDown = true
                    return card
                })
                s.waste = []
                s.stock = moveToStock.reverse()
            })

            // Animate the top few cards sliding from waste position to stock position
            // Only animate the last few visible cards (not all 24) to keep it snappy
            const numToAnimate = Math.min(3, committed.stock.length)
            const stockLen = committed.stock.length
            const animEntries = []
            for (let idx = 0; idx < numToAnimate; idx++) {
                // The waste cards were reversed, so the last waste card is now first in stock
                // wasteFromPositions are in original waste order; reversed stock maps back
                const wasteIdx = wasteFromPositions.length - 1 - idx
                animEntries.push({
                    card: committed.stock[stockLen - 1 - idx],
                    fromX: wasteFromPositions[wasteIdx].fromX,
                    fromY: wasteFromPositions[wasteIdx].fromY,
                })
            }
            animationManager.animateCards(animEntries, 200)
            playSound('recycle')
            hapticDrop()
        }
    }

    schedulePaint()
}

export function canvasTouchMove(e) {
    // Cancel any in-flight animations when dragging
    if (animationManager.isAnimating()) {
        animationManager.clearAll()
    }

    e.preventDefault()
    const touch = e.changedTouches[0]

    // document.getElementById('mouseCoordinates').innerText = `X: ${touch.clientX}
    // Y: ${touch.clientY}`
    const touchX = touch.clientX
    const touchY = touch.clientY
    const distanceX = touchX - startX
    const distanceY = touchY - startY

    if (getDraggedCards().length > 0) {
        const movedCards = getDraggedCards().map((card) => {
            const newX = card.originalX + distanceX
            const newY = card.originalY + distanceY
            // Write to the drag overlay instead of mutating card.x/y
            setDragPosition(card, Math.floor(newX), Math.floor(newY))
            return card
        })
        setDraggedCards(movedCards)
    }

    schedulePaint()
}

export function canvasTouchEnd(e) {
    // Cancel any in-flight animations so the drop processes immediately
    if (animationManager.isAnimating()) {
        animationManager.clearAll()
    }

    const touch = e.changedTouches[0]

    // If menu is open, ignore touch end on the canvas (menu handles its own taps in touchStart)
    if (appState.currentAppState === CurrentAppState.Menu) {
        return
    }

    e.preventDefault()

    startX = undefined
    startY = undefined

    const draggedCards = getDraggedCards()
    if (draggedCards && draggedCards.length > 0) {
        playSound('place')

        // console.log(draggedCards)
        // figure out what we're dragged over.
        // We only care about being over a foundation or a tableau.
        // Use bounding-box overlap detection (card visual position vs target),
        // with fallback to touch-point detection for edge cases.

        const isOverFoundation =
            findOverlapFoundation(draggedCards) ?? isCardOverFoundation(touch.clientX, touch.clientY)
        // 1. are we over a foundation?
        // 2. can we place our card on the top card?
        const isOverTableau =
            findOverlapTableau(draggedCards) ?? isCardOverTableau(touch.clientX, touch.clientY)
        // console.log({ isOverTableau })

        if (isOverFoundation !== undefined) {
            const bottomCardOnDraggedCards = draggedCards[0]
            const currentFoundation = getCurrentCardState().foundation[isOverFoundation]
            // Foundation only accepts single cards, not stacks
            if (
                draggedCards.length === 1 &&
                canPlaceOnFoundation(bottomCardOnDraggedCards, currentFoundation)
            ) {
                // Save pre-drop position for animation
                const dropPos = getCardPosition(draggedCards[0])
                const fromX = dropPos.x
                const fromY = dropPos.y

                const committed = commitMove(
                    (s) => {
                        s.foundation[isOverFoundation].push(draggedCards[0])
                    },
                    { popFirst: true, skipIntegrity: true }
                )

                // Clear dragged cards BEFORE animation starts — otherwise paint()
                // inside tick() draws the card twice (once from state, once from draggedCards)
                setDraggedCards([])

                // Animate card from drop position to final foundation position
                const fPile = committed.foundation[isOverFoundation]
                const movedCard = fPile[fPile.length - 1]
                animationManager.animateCard(movedCard, fromX, fromY, 200)
                hapticDrop()
            } else {
                animatedUndo()
            }
        }
        // 1.are we over a tableau
        // can we place our card onto the top card there?
        else if (isOverTableau !== undefined) {
            // Save pre-drop positions for animation
            const draggedFromPositions = draggedCards.map((c) => {
                const pos = getCardPosition(c)
                return { fromX: pos.x, fromY: pos.y }
            })

            const bottomCardOnDraggedCards = draggedCards[0]
            const currentTableau = getCurrentCardState().tableau[isOverTableau]
            const canPlace = canPlaceOnTableau(bottomCardOnDraggedCards, currentTableau)
            if (canPlace) {
                const committed = commitMove(
                    (s) => {
                        s.tableau[isOverTableau] = [...s.tableau[isOverTableau], ...draggedCards]
                    },
                    { popFirst: true, skipIntegrity: true }
                )

                // Animate dragged cards from drop position to final tableau position
                const tableauPile = committed.tableau[isOverTableau]
                const startIdx = tableauPile.length - draggedCards.length
                const animEntries = draggedCards.map((_, idx) => ({
                    card: tableauPile[startIdx + idx],
                    fromX: draggedFromPositions[idx].fromX,
                    fromY: draggedFromPositions[idx].fromY,
                }))

                // Clear dragged cards BEFORE animation starts — otherwise paint()
                // inside tick() draws the card twice (once from state, once from draggedCards)
                setDraggedCards([])

                animationManager.animateCards(animEntries, 200)
                hapticDrop()
            } else {
                animatedUndo()
            }
        } else {
            // Pop the intermediate pickup state to revert the card to its original position
            animatedUndo()
        }
    }
    // setDraggedCards([]) is now called before each animation path above,
    // and undo() inside animatedUndo() also clears draggedCards.
    // This final call is a safety net for any edge case paths.
    setDraggedCards([])
    clearAllDragPositions()

    if (appState.currentAppState === CurrentAppState.Playing) {
        handleWinCheck()
    }

    schedulePaint()
}

export function canvasTouchDoubleTap(e) {
    // Reentrant lock — prevents double-fire from timing-based detection + native dblclick
    if (_isProcessingDoubleTap) return
    _isProcessingDoubleTap = true

    try {
        // Cancel any in-flight animations so the double-tap processes immediately
        if (animationManager.isAnimating()) {
            animationManager.clearAll()
        }

        e.preventDefault()
        const touch = e.changedTouches[0]
        const selectedCard = getSelectedCard(touch.clientX, touch.clientY)

        // The gate in canvasTouchStart guarantees: selectedCard is defined, face-up, and not stock.
        if (appState.currentAppState !== CurrentAppState.Playing || selectedCard === undefined) return

        const currentState = getCurrentCardState()
        const tableauIndex = cardIndexTableau(selectedCard)
        const wasteIndex = cardIndexWaste(selectedCard)

        // Determine if this is a valid top card (tableau top or waste top)
        let isTopCard = false
        if (tableauIndex !== undefined) {
            const [ti, tj] = tableauIndex
            isTopCard = tj === currentState.tableau[ti].length - 1
        } else if (wasteIndex !== undefined) {
            isTopCard = true // waste hit-detection already only returns the top card
        }

        if (!isTopCard) return

        // Try to place on any foundation
        const fromPos = getCardPosition(selectedCard)
        for (let i = 0; i < currentState.foundation.length; i++) {
            if (canPlaceOnFoundation(selectedCard, currentState.foundation[i])) {
                if (tableauIndex !== undefined) {
                    const [ti, tj] = tableauIndex
                    const committed = commitMove((s) => {
                        const card = s.tableau[ti][tj]
                        s.foundation[i].push(card)
                        s.tableau[ti] = s.tableau[ti].slice(0, tj)
                    })
                    const movedCard = committed.foundation[i][committed.foundation[i].length - 1]
                    animationManager.animateCard(movedCard, fromPos.x, fromPos.y)
                } else {
                    const committed = commitMove((s) => {
                        const card = s.waste.pop()
                        s.foundation[i].push(card)
                        decrementWasteVisible(s, appState.NUM_CARDS_FLIPPED)
                    })
                    const movedCard = committed.foundation[i][committed.foundation[i].length - 1]
                    animationManager.animateCard(movedCard, fromPos.x, fromPos.y)
                }
                break
            }
        }

        // Check for win after double-tap moves a card to foundation
        if (!handleWinCheck()) {
            // Only play the lighter sound when it's not a win
            playSound('autoFoundation')
        }

        hapticDrop()
        schedulePaint()
    } finally {
        _isProcessingDoubleTap = false
    }
}

/**
 * Handle touch cancellation (e.g. iOS system gesture, notification banner).
 * Cleans up dragged cards and reverts any in-progress pickup.
 */
export function canvasTouchCancel() {
    // Cancel any in-flight animations
    if (animationManager.isAnimating()) {
        animationManager.clearAll()
    }

    // If cards were being dragged, undo the pickup
    if (getDraggedCards().length > 0) {
        animatedUndo()
    }

    setDraggedCards([])
    clearAllDragPositions()

    startX = undefined
    startY = undefined

    // Reset double-tap tracking to prevent stale state
    myLatestTap = 0

    schedulePaint()
}
